import { RefObject, useEffect, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';

import Icon from '@components/icon/Icon';
import Tooltip from '@components/tooltip/Tooltip';
import testId from '@constants/testId';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import { ActionsProps } from '@features/transactions/types';
import useScrollIntoViewWithHeader from '@hooks/useScrollIntoViewWithHeader';
import { downloadQRCode } from '@utils/downloadFile';
import useOnClickOutside from '@utils/hooks';

import theme from '../../../themes/colors';

interface ThreeDotActionButtonProps {
  id: string;
  actions?: ActionsProps[];
  isDisabled?: boolean;
  onToggle?: (isOpen: boolean) => void;
  source?: string;
  stroke?: string;
  isLast?: boolean;
  isFirstRow?: boolean;
  iconPosition?: 'left' | 'right';
  modalStyle?: string;
}

const disableButtonStyle = '!text-grey opacity-50 cursor-not-allowed';

const ThreeDotActionButton = ({
  id,
  actions,
  isDisabled,
  onToggle,
  source,
  stroke = theme.grey,
  isLast = false,
  isFirstRow = false,
  iconPosition = 'right',
  modalStyle = ''
}: ThreeDotActionButtonProps) => {
  const ref = useRef(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [tooltipText, setTooltipText] = useState<string>('');
  const [hoveredActionId, setHoveredActionId] = useState<string>('');
  const [openAbove, setOpenAbove] = useState<boolean>(false);
  const lastActionRef = useRef<HTMLDivElement>(null);

  const handleClick = (event: React.MouseEvent) => {
    if (isDisabled) return;
    event.stopPropagation();

    const buttonRect = (event.target as HTMLElement).getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    if (!isFirstRow && (viewportHeight - buttonRect.bottom < 250 || isLast)) {
      setOpenAbove(true);
    } else {
      setOpenAbove(false);
    }

    setModalOpen((prev) => !prev);
  };

  useOnClickOutside(ref, (e) => {
    setModalOpen(false);
    e.stopPropagation();
  });

  useEffect(() => {
    onToggle?.(modalOpen);
  }, [onToggle, modalOpen]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (tooltipText !== '') setTooltipText('');
    }, 1000);

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [tooltipText]);

  useScrollIntoViewWithHeader({
    elementRef: lastActionRef,
    shouldScroll: modalOpen
  });

  return (
    <div
      className="relative max-w-8"
      ref={ref}
      data-testid={testId.transactionsPage.overflowMenu}
    >
      <div
        role="presentation"
        key={id}
        onClick={(event) => handleClick(event)}
        className={`p-2 ${
          isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
        }`}
      >
        <Icon
          name="three-dots-grey"
          stroke={stroke}
          className={isDisabled ? 'opacity-30' : ''}
        />
      </div>

      {modalOpen && (
        <div
          className={`absolute right-4 ${
            openAbove ? 'bottom-8' : 'top-8'
          } z-40 w-55 rounded-lg bg-white shadow-md ${modalStyle}`}
        >
          {actions?.map((action, index) => {
            if (action.onClick || action.qrLink)
              return (
                <div key={action.id} className="relative">
                  <div
                    role="presentation"
                    className={`relative flex items-center gap-2 p-3 ${
                      !action.isDisabled ? 'hover:bg-gray-50' : ''
                    } ${
                      index === 0 ? 'rounded-t-lg' : 'border-t border-grey40'
                    } ${action.isDisabled ? `${disableButtonStyle}` : ''} ${
                      action.style ?? ''
                    } ${index + 1 !== actions.length ? '' : 'rounded-b-lg'}`}
                    onClick={(event) => {
                      if (action.isDisabled) {
                        event.preventDefault();
                        event.stopPropagation();
                        return;
                      }
                      if (action.tooltipText)
                        setTooltipText(action.tooltipText);
                      if (action.downloadQrCode) {
                        if (source)
                          sendMixpanelEvent(
                            Event.DOWNLOAD_QR_CODE_IN_EVENTS_CARD
                          );
                        event.stopPropagation();
                        downloadQRCode(
                          canvasRef as RefObject<HTMLDivElement>,
                          'qr code'
                        );
                      }
                      action.onClick?.(event);
                    }}
                    onMouseEnter={() => setHoveredActionId(action.id || '')}
                    onMouseLeave={() => setHoveredActionId('')}
                    data-tooltip-id={
                      action.isDisabled && action.disabledTooltip
                        ? `disabled-tooltip-${action.id}`
                        : `copy-tooltip-${action.id}`
                    }
                  >
                    {iconPosition === 'left' && action.iconName && (
                      <Icon
                        name={action.iconName}
                        stroke={theme.primary}
                        className={action.isDisabled ? 'opacity-50' : ''}
                      />
                    )}
                    {action.label}
                    {iconPosition === 'right' && action.iconName && (
                      <Icon
                        name={action.iconName}
                        stroke={theme.primary}
                        className={action.isDisabled ? 'opacity-50' : ''}
                      />
                    )}
                    {tooltipText && action.tooltipText && (
                      <Tooltip
                        id={`copy-tooltip-${action.id}`}
                        type="text"
                        showTooltip={tooltipText !== ''}
                        tooltipText="Copied!"
                      />
                    )}
                    {action.qrLink && (
                      <div ref={canvasRef} className="hidden">
                        <QRCodeCanvas
                          value={action.qrLink}
                          bgColor="white"
                          fgColor="black"
                          marginSize={4}
                        />
                      </div>
                    )}
                  </div>
                  {action.isDisabled &&
                    action.disabledTooltip &&
                    hoveredActionId === action.id && (
                      <Tooltip
                        id={`disabled-tooltip-${action.id}`}
                        type="text"
                        showTooltip
                        tooltipText={action.disabledTooltip}
                        variant="dark"
                        place="bottom"
                        className="!italic"
                      />
                    )}
                </div>
              );

            return null;
          })}
          {/* reference div to scroll to the last action in the modal */}
          <div ref={lastActionRef} />
        </div>
      )}
    </div>
  );
};

export default ThreeDotActionButton;
